import * as React from "react";
import { InputAdornment, TextField, IconButton } from "@mui/material";
import { observer } from "mobx-react";
import * as _ from "lodash";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { useRootStore } from "../../../store/contexts";
import { VisibilityIcon } from "../../../icons";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export const idForAdding = "add-new";

export type InputReferenceType = {
  displayId: string;
  id: string;
  inputValue?: string;
};
const filter = createFilterOptions<InputReferenceType>();

export type InputReferenceProps = {
  id: string;
  selectedValue?: InputReferenceType | null;
  onChange: (value: string) => void;
  onSelect: (value: InputReferenceType | null) => void;
  options?: InputReferenceType[];
  error?: boolean;
  helperText?: string;
  label?: string;
  icon?: React.ReactElement;
  onView?: () => void;
  autoFocus?: boolean;
  customWidth?: number;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  canCreate?: boolean;
  nullText?: string;
  renderCreateDialog?: (props: {
    open: boolean;
    initialValue: string;
    onCreated: (value: { displayId: string; id: string }) => void;
    onCancelled: () => void;
  }) => React.ReactNode;
};

export const InputReference: React.FC<InputReferenceProps> = observer(
  ({
    onChange,
    onSelect,
    selectedValue,
    options = [],
    error,
    helperText,
    label,
    onView,
    autoFocus = false,
    customWidth = 500,
    disabled = false,
    hidden = false,
    description,
    canCreate = true,
    nullText,
    renderCreateDialog,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [isReadOnly, setIsReadOnly] = React.useState(true);
    const [focused, setFocused] = React.useState(false);
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [createDialogInitialValue, setCreateDialogInitialValue] =
      React.useState("");
    const [inputValue, setInputValue] = React.useState("");

    if (hidden) return null;

    const isEmpty = !selectedValue;
    const showLabelInside = description && isEmpty && !focused;
    const baseLabel =
      nullText && isEmpty && !focused ? `${label} (${nullText})` : label;
    const displayLabel = showLabelInside ? undefined : baseLabel;
    const effectivePlaceholder = showLabelInside
      ? nullText
        ? `${label} (${nullText})`
        : label
      : !label
      ? `${langStore.searchCommand}...`
      : undefined;

    return (
      <>
        <Autocomplete
          disabled={disabled}
          options={options}
          value={selectedValue}
          inputValue={inputValue}
          onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
          slotProps={{
            popper: {
              sx: { zIndex: 10300 },
            },
          }}
          isOptionEqualToValue={(option: any, value: any) =>
            value !== "" && option !== "" && option.id === value.id
          }
          onChange={(_event: any, newValue: any) => {
            if (newValue?.id === "add-new" && renderCreateDialog) {
              const notFound = /".*?"/.exec(newValue.displayId);
              setCreateDialogInitialValue(
                notFound ? notFound[0].replaceAll('"', "") : ""
              );
              setCreateDialogOpen(true);
              setInputValue("");
              return;
            }
            onSelect(newValue);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          sx={{
            m: 1,
            width: customWidth,
            "& .MuiInputBase-root": {
              paddingRight: "50px !important",
            },
          }}
          getOptionLabel={(option: any) => option.displayId}
          noOptionsText={`${langStore.noDataMessage}`}
          renderInput={(params) => {
            const randomId = `ref-${Math.random().toString(36).substr(2, 9)}`;

            return (
              <TextField
                {...params}
                autoFocus={autoFocus}
                size="small"
                variant="outlined"
                fullWidth
                error={error}
                label={
                  <FieldLabelWithHelp
                    label={displayLabel}
                    description={description}
                  />
                }
                InputLabelProps={description ? { shrink: true } : undefined}
                helperText={helperText}
                onChange={_.debounce(
                  (event) => onChange(event.target.value),
                  500
                )}
                placeholder={effectivePlaceholder}
                inputProps={{
                  ...params.inputProps,
                  readOnly: isReadOnly,
                  autoComplete: "one-time-code",
                  name: randomId,
                  id: randomId,
                  "data-form-type": "other",
                  onFocus: (e: any) => {
                    setIsReadOnly(false);
                    e.target.removeAttribute("readonly");
                    params.inputProps.onFocus?.(e);
                  },
                  onBlur: (e: any) => {
                    setIsReadOnly(true);
                    e.target.setAttribute("readonly", "readonly");
                    params.inputProps.onBlur?.(e);
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: onView ? (
                    <>
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onView}
                          sx={{ color: "text.secondary" }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </>
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
              />
            );
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            if (canCreate && options.length === 0 && inputValue !== "") {
              return [
                {
                  id: "add-new",
                  inputValue,
                  displayId: `${langStore.addCommand} "${inputValue}"`,
                },
              ];
            }
            return filtered;
          }}
          renderOption={(props, option, { inputValue }) => {
            const matches = match(option.displayId, inputValue, {
              insideWords: true,
            });
            const parts = parse(option.displayId, matches);
            let extraStyle = {};
            if (option.id === idForAdding) {
              extraStyle = { color: "darkGreen", fontWeight: 700 };
            }
            return (
              <li {...props}>
                <div>
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{
                        fontWeight: part.highlight ? 700 : 400,
                        ...extraStyle,
                      }}
                    >
                      {part.text}
                    </span>
                  ))}
                </div>
              </li>
            );
          }}
        />
        {renderCreateDialog &&
          renderCreateDialog({
            open: createDialogOpen,
            initialValue: createDialogInitialValue,
            onCreated: (value) => {
              setCreateDialogOpen(false);
              onSelect(value);
            },
            onCancelled: () => {
              setCreateDialogOpen(false);
            },
          })}
      </>
    );
  }
);
